import React from 'react';
import { Link, useParams } from 'react-router-dom';
import css from './GoalCard.module.scss';
import { GoalAccomplishmentExtendedDto } from 'src/api/v2';
import { ROUTES } from 'src/navigation';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { formatNOKCurrency } from 'src/utils/format';
import { Card, Paragraph, ProgressBar } from '@innovationnorway/cl-react';

type Props = {
  link: boolean;
  goal: GoalAccomplishmentExtendedDto;
};

const GoalCard = ({ link, goal }: Props) => {
  const { year } = useParams();
  const { t } = useTranslation('reportingNav');
  const { t: tSiva } = useTranslation('siva');
  const filledTargetNumbers = goal.targetNumbers?.filter((target) => target.valueDto).length;
  const totalTargetNumbers = goal.targetNumbers?.length || 0;
  const hasProgress = goal.goalDetail.requiresTargetNumber !== false;
  const showEditedCount =
    !goal.goalDetail.isOperatorLevel &&
    filledTargetNumbers !== undefined &&
    (!hasProgress || !!goal.goalDetail.basedOnTargetNumberInput);

  return (
    <Card variant="tinted" data-size="sm" className="height--100">
      <Card.Block className="display--flex justify-content--space-between gap--2">
        <Paragraph className="margin--0">{goal.goalDetail.name}</Paragraph>

        {link && year && (
          <Link
            to={ROUTES.SIVA.REPORTING.ACHIEVED.YEAR(year).ACTIVITIES + `/${goal.id}|${goal.goalTemplateId}`}
          >
            {t('achieved.viewDetails')}
          </Link>
        )}
      </Card.Block>
      <Card.Block>
        {hasProgress ? (
          <>
            <ProgressBar
              className={css['goal-achievement__progress-bar']}
              value={goal.expectedValue > 0 ? (goal.value / goal.expectedValue) * 100 : 0}
              aria-label={t('achieved.progressLabel', {
                name: goal.goalDetail.name,
                value: goal.value,
                expected: goal.expectedValue,
              })}
            />
            <div className="display--flex justify-content--space-between">
              <Paragraph className="margin--0">
                {t('achieved.value', {
                  value: `${goal.value}${goal.goalDetail.isPercentage ? '%' : ''}`,
                })}
              </Paragraph>
              <Paragraph className="margin--0">
                {t('achieved.goalValue', {
                  value: `${goal.expectedValue}${goal.goalDetail.isPercentage ? '%' : ''}`,
                })}
              </Paragraph>
            </div>
          </>
        ) : (
          <Paragraph className="margin--0">
            {t('achieved.valueLabel')}:{' '}
            <span className="font-weight--bold">
              {goal.goalDetail.code === 'PaidEquity' ? formatNOKCurrency(goal.value) : goal.value}
            </span>
          </Paragraph>
        )}
        {showEditedCount && (
          <Paragraph className="margin--0 margin-top--2">
            {tSiva('goalAccomplishment.edited')}:{' '}
            <span
              className={classNames([
                css['goal-achievement__number'],
                filledTargetNumbers >= totalTargetNumbers ? 'background-color--green-50' : '',
              ])}
            >
              {filledTargetNumbers}
            </span>{' '}
            {t('achieved.ofTotal', { total: totalTargetNumbers })}
          </Paragraph>
        )}
      </Card.Block>
    </Card>
  );
};

export default GoalCard;
